import { useState, useEffect } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { CustomerAsyncSelect, DokanModal } from '@dokan/components';

type Customer = {
    id: number;
    name: string;
    email: string;
    phone: string;
    company: string;
    shipping?: {
        country: string;
        state: string;
        address_1: string;
        address_2: string;
        city: string;
        postcode: string;
    };
};

type CustomerInfoModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSave: ( customer: Customer ) => void;
    customer: Customer | null;
};

const CustomerInfoModal = ( {
    isOpen,
    onClose,
    onSave,
    customer,
}: CustomerInfoModalProps ) => {
    const defaultCustomer: Customer = {
        id: 0,
        name: '',
        email: '',
        phone: '',
        company: '',
    };
    const [ formData, setFormData ] = useState< Customer >( {
        ...defaultCustomer,
        ...customer,
    } );
    const [ error, setError ] = useState< string | null >( null );

    const handleChange = ( field: keyof Customer, value: string ) => {
        setFormData( { ...formData, [ field ]: value } );
        if ( field === 'name' && value.trim() ) {
            setError( null );
        }
    };

    const handleSave = () => {
        if ( ! formData.id && ! formData.name.trim() ) {
            setError( __( 'Quote user is required.', 'dokan' ) );
            return;
        }
        onSave( formData );
    };

    const isUpdateDisabled = ! formData.id && ! formData.name;

    return (
        <DokanModal
            isOpen={ isOpen }
            onClose={ onClose }
            className="w-[620px]"
            namespace="customer-info-modal"
            onConfirm={ handleSave }
            confirmButtonDisabled={ isUpdateDisabled }
            dialogTitle={ __( 'Editing Customer Information', 'dokan' ) }
            confirmButtonText={ __( 'Update', 'dokan' ) }
            cancelButtonText={ __( 'Cancel', 'dokan' ) }
            dialogContent={
                <div className="space-y-5 px-1 py-2">
                    <div className="customer-information-section">
                        { /* eslint-disable-next-line jsx-a11y/label-has-associated-control */ }
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            { __( 'Quote user', 'dokan' ) }{ ' ' }
                            <span className="text-[#E64B5F]">
                                { __( '(Required)', 'dokan' ) }
                            </span>
                        </label>
                        <CustomerAsyncSelect
                            value={
                                formData.id || formData.name
                                    ? {
                                          value: formData.id,
                                          label: formData.name,
                                      }
                                    : null
                            }
                            onChange={ ( opt: any ) => {
                                if ( opt ) {
                                    setFormData( {
                                        ...formData,
                                        id: opt.value,
                                        name:
                                            opt.raw.first_name &&
                                            opt.raw.last_name
                                                ? `${ opt.raw.first_name } ${ opt.raw.last_name }`
                                                : opt.raw.display_name ||
                                                  opt.raw.email,
                                        email: opt.raw.email || '',
                                        phone:
                                            opt.raw?.billing?.phone ||
                                            formData.phone ||
                                            '',
                                        company:
                                            opt.raw?.billing?.company ||
                                            formData.company ||
                                            '',
                                        shipping: opt.raw.billing
                                            ? {
                                                  country:
                                                      opt.raw?.billing
                                                          ?.country || '',
                                                  state:
                                                      opt.raw?.billing?.state ||
                                                      '',
                                                  address_1:
                                                      opt.raw?.billing
                                                          ?.address_1 || '',
                                                  address_2:
                                                      opt.raw?.billing
                                                          ?.address_2 || '',
                                                  city:
                                                      opt.raw?.billing?.city ||
                                                      '',
                                                  postcode:
                                                      opt.raw?.billing
                                                          ?.postcode || '',
                                              }
                                            : undefined,
                                    } );
                                    setError( null );
                                } else {
                                    setFormData( {
                                        ...formData,
                                        id: 0,
                                        name: '',
                                        email: '',
                                    } );
                                    setError(
                                        __( 'Quote user is required.', 'dokan' )
                                    );
                                }
                            } }
                            endpoint="dokan/v1/request-for-quote/customers"
                            placeholder={ __( 'Select Customer', 'dokan' ) }
                            isClearable
                            defaultOptions
                        />
                        { error && (
                            <p className="mt-2 text-xs text-[#E64B5F]">
                                { error }
                            </p>
                        ) }
                    </div>

                    <div>
                        { /* eslint-disable-next-line jsx-a11y/label-has-associated-control */ }
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            { __( 'Full Name', 'dokan' ) }
                        </label>
                        <input
                            type="text"
                            value={ formData.name }
                            readOnly
                            placeholder={ __( 'Mr. John Doe', 'dokan' ) }
                            className="w-full px-3 py-2.5 text-sm border border-[#e5e7eb] rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-gray-50 cursor-not-allowed"
                        />
                    </div>

                    <div>
                        { /* eslint-disable-next-line jsx-a11y/label-has-associated-control */ }
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            { __( 'Email', 'dokan' ) }
                        </label>
                        <input
                            type="email"
                            value={ formData.email }
                            readOnly
                            placeholder={ __( 'udbyfunni@gmail.com', 'dokan' ) }
                            className="w-full px-3 py-2.5 text-sm border border-[#e5e7eb] rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-gray-50 cursor-not-allowed"
                        />
                    </div>

                    <div>
                        { /* eslint-disable-next-line jsx-a11y/label-has-associated-control */ }
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            { __( 'Company Name', 'dokan' ) }
                        </label>
                        <input
                            type="text"
                            value={ formData.company }
                            onChange={ ( e ) =>
                                handleChange( 'company', e.target.value )
                            }
                            placeholder={ __( 'Write here', 'dokan' ) }
                            className="w-full px-3 py-2.5 text-sm border border-[#e5e7eb] rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                    </div>

                    <div>
                        { /* eslint-disable-next-line jsx-a11y/label-has-associated-control */ }
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            { __( 'Phone Number', 'dokan' ) }
                        </label>
                        <input
                            type="tel"
                            value={ formData.phone }
                            onChange={ ( e ) =>
                                handleChange( 'phone', e.target.value )
                            }
                            placeholder={ __( 'e.g +1 555-123-4567', 'dokan' ) }
                            className="w-full px-3 py-2.5 text-sm border border-[#e5e7eb] rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                    </div>
                </div>
            }
        />
    );
};

export default CustomerInfoModal;
